import {
  mapPartnerGroupChannelSocketMessage,
  mapPartnerGroupChannelSocketReactionUpdate,
  toPartnerGroupChannelListingMessage,
} from '../components/Partner/Community/mapPartnerGroupChannel';
import { COMMUNITY_NOTIFICATION_TYPE } from '../constants/communityNotificationTypes';
import { SOCKET_EVENTS } from '../constants/socketEvents';

const toDataString = (value) => {
  if (value == null) return '';
  return String(value);
};

/** Prefer `data` (message:new) then legacy `payload` (partner group chat). */
export const getSocketEnvelopeBody = (envelope = {}) => {
  if (!envelope || typeof envelope !== 'object') return {};
  if (envelope.data && typeof envelope.data === 'object') return envelope.data;
  if (envelope.payload && typeof envelope.payload === 'object') return envelope.payload;
  return {};
};

const seenIdempotencyKeys = new Set();
const MAX_SEEN_IDEMPOTENCY_KEYS = 400;

/** Returns true when this idempotency key was already handled. */
export const consumeSocketIdempotencyKey = (idempotencyKey) => {
  const key = String(idempotencyKey ?? '').trim();
  if (!key) return false;
  if (seenIdempotencyKeys.has(key)) return true;

  seenIdempotencyKeys.add(key);
  if (seenIdempotencyKeys.size > MAX_SEEN_IDEMPOTENCY_KEYS) {
    const oldest = seenIdempotencyKeys.values().next().value;
    seenIdempotencyKeys.delete(oldest);
  }
  return false;
};

/** @private test helper */
export const resetSocketIdempotencyKeysForTests = () => {
  seenIdempotencyKeys.clear();
};

/**
 * Map the standard socket envelope into the FCM-shaped payload used by push UI + routing.
 */
export const mapPartnerGroupChatSocketToPushPayload = (envelope = {}) => {
  const body = getSocketEnvelopeBody(envelope);
  const title = body.title || body.channelName || 'Partner Community';
  const listing = toPartnerGroupChannelListingMessage(body);
  // Keep notification body as "Name: text" when the server sent that shape; chat UI uses listing.text.
  const message = body.message ?? listing?.text ?? body.messagePreview ?? body.text ?? '';

  return {
    notification: { title },
    data: {
      type: body.type || COMMUNITY_NOTIFICATION_TYPE.PARTNER_GROUP_CHAT_MESSAGE,
      notificationId: toDataString(body.notificationId || envelope.idempotencyKey),
      title: toDataString(title),
      message: toDataString(message),
      body: toDataString(message),
      channelId: toDataString(body.channelId),
      channelName: toDataString(body.channelName),
      messageId: toDataString(body.messageId ?? body.id),
      messagePreview: toDataString(
        body.messagePreview ?? listing?.text ?? body.message ?? body.text,
      ),
      isGroupChat: toDataString(body.isGroupChat ?? true),
      targetType: COMMUNITY_NOTIFICATION_TYPE.PARTNER_GROUP_CHAT_MESSAGE,
      timestamp: toDataString(envelope.timestamp),
      senderId: toDataString(body.sender?.id),
      senderUid: toDataString(body.sender?.uid),
      senderFullName: toDataString(body.sender?.fullName),
    },
  };
};

export const isPartnerGroupChatSocketEvent = (eventName) =>
  eventName === SOCKET_EVENTS.PARTNER_GROUP_CHAT_MESSAGE;

export const isPartnerGroupChatReactionSocketEvent = (eventName) =>
  eventName === SOCKET_EVENTS.PARTNER_GROUP_CHAT_REACTION ||
  eventName === SOCKET_EVENTS.MESSAGE_REACTION_ADDED ||
  eventName === SOCKET_EVENTS.MESSAGE_REACTION_REMOVED;

export const isMessageNewSocketEvent = (eventName) => eventName === SOCKET_EVENTS.MESSAGE_NEW;

export const getPartnerGroupChatSenderUid = (envelope = {}) =>
  getSocketEnvelopeBody(envelope)?.sender?.uid ?? null;

/** All actor identifiers present on a reaction envelope (firebase / stream / db). */
export const collectPartnerGroupChatReactionActorIds = (envelope = {}) => {
  const body = getSocketEnvelopeBody(envelope);
  const candidates = [
    body?.actor?.uid,
    body?.actor?.id,
    body?.sender?.uid,
    body?.sender?.id,
    body?.actorUid,
    body?.actorId,
    body?.userId,
  ];

  const ids = [];
  const seen = new Set();
  for (const value of candidates) {
    const id = String(value ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
};

export const getPartnerGroupChatReactionActorUid = (envelope = {}) =>
  collectPartnerGroupChatReactionActorIds(envelope)[0] ?? null;

export const getPartnerGroupChatSocketChannelId = (envelope = {}) => {
  const channelId = getSocketEnvelopeBody(envelope)?.channelId;
  const trimmed = String(channelId ?? '').trim();
  return trimmed || null;
};

export const getPartnerGroupChatSocketMessageId = (envelope = {}) => {
  const body = getSocketEnvelopeBody(envelope);
  const messageId = body?.messageId ?? body?.id;
  const trimmed = String(messageId ?? '').trim();
  return trimmed || null;
};

/** @deprecated Use mapPartnerGroupChannelSocketMessage from mapPartnerGroupChannel.js */
export const mapPartnerGroupChatSocketToChatMessage = (envelope = {}, mapOptions = {}) =>
  mapPartnerGroupChannelSocketMessage(envelope, mapOptions);

/** @deprecated Use mapPartnerGroupChannelSocketReactionUpdate from mapPartnerGroupChannel.js */
export const mapPartnerGroupChatSocketToReactionUpdate = (envelope = {}, mapOptions = {}) =>
  mapPartnerGroupChannelSocketReactionUpdate(envelope, mapOptions);

/**
 * Skip socket fanout for the signed-in user's own reaction (optimistic UI already applied).
 * Matches firebase uid, stream userId, actorId, and any id in currentUserIds.
 */
export const shouldSkipOwnReactionSocketEvent = (envelope = {}, firebaseUid, currentUserIds) => {
  const actorIds = collectPartnerGroupChatReactionActorIds(envelope);
  if (!actorIds.length) {
    return false;
  }

  if (firebaseUid && actorIds.includes(String(firebaseUid).trim())) {
    return true;
  }

  if (currentUserIds instanceof Set && currentUserIds.size > 0) {
    return actorIds.some((id) => currentUserIds.has(id));
  }

  return false;
};
